/**
 * Reads who a bank transaction was with, how it was paid and its reference, from the narration.
 * Covers the common Indian formats (IMPS / NEFT / RTGS / UPI / mobile-banking / cheque / cash).
 *
 * parseNarration("IMPS/612345678901/RAVI TEXTI/SBIN/XX1234/Busine")
 *   → { mode: "IMPS", name: "RAVI TEXTI", ref: "612345678901", bank: "SBIN", accountTail: "1234", ... }
 */

// Words that are never part of a counterparty name
const NOISE = new Set([
  "IMPS", "NEFT", "RTGS", "UPI", "MPAY", "MBANK", "MBK", "INST", "INB", "IB", "TRF", "TRANSFER", "TO", "FROM", "BY",
  "CR", "DR", "BUSINE", "BUSINESS", "PAYMENT", "TRANSACTION", "BANK", "NO", "NOT", "IMPS TRANSACTION", "SELL", "SEL",
  "OKAXIS", "OKICICI", "OKHDFCBANK", "OKSBI", "YBL", "PAYTM", "IBL", "AXL", "APL", "UPI REF", "REF", "MR", "MRS", "MS", "M/S"
]);
const BANK_CODES = /^(SBIN|HDFC|ICIC|UTIB|KVBL|TMBL|IOBA|IDIB|CNRB|BKID|BARB|PUNB|UBIN|KKBK|YESB|IDFB|FDRL|CIUB|IBKL|INDB|AUBL|SIBL|CSBK|BANK)/i;

const clean = (s) => String(s || "").replace(/\s+/g, " ").trim();
const isRef = (s) => /^[A-Z]{0,6}\d{6,}[A-Z0-9]*$/i.test(String(s).replace(/\s+/g, ""));
const isNoise = (s) => NOISE.has(clean(s).toUpperCase()) || /^XX\d{2,}$/i.test(s) || /^[\d\W]+$/.test(s);

/** Turns a name into a comparable key: upper-case letters/digits, single spaces, no titles. */
export function normalizeName(name) {
  return clean(String(name || "").toUpperCase().replace(/[^A-Z0-9 ]+/g, " "))
    .replace(/^(M\s*S|MR|MRS|MS|SRI|SHRI|THE)\s+/, "")
    .replace(/\s+(PVT|PRIVATE|LTD|LIMITED|CO|COMPANY|AND)$/g, "")
    .trim();
}

function firstName(parts) {
  const candidate = parts.map(clean).find((p) => p && !isRef(p) && !isNoise(p) && !BANK_CODES.test(p) && !p.includes("@"));
  return candidate || "";
}

/** App payment mode for a narration mode. */
export function paymentModeFor(mode) {
  if (mode === "UPI") return "UPI / GPay / PhonePe";
  if (mode === "CHEQUE") return "Cheque";
  if (mode === "CASH" || mode === "ATM") return "Cash";
  return "Bank Transfer / NEFT";
}

export function parseNarration(description) {
  const text = clean(description);
  const upper = text.toUpperCase();
  const result = { mode: "OTHER", name: "", ref: "", bank: "", accountTail: "", vpa: "", raw: text };
  const tail = text.match(/XX(\d{3,6})/i);
  if (tail) result.accountTail = tail[1];
  const vpa = text.match(/[A-Za-z0-9.\-_]{2,}@[A-Za-z]{2,}/);
  if (vpa) result.vpa = vpa[0].toLowerCase();

  // Bank's own entries: charges, taxes, interest, forex markup → not a party
  if (/(INT\.?\s*COLL|INTEREST|INT\s*PD|CHARGES|CHGS|SMS\s*CHG|FOLIO|\bDPO\b|\bRFX\b|\bCGST\b|\bSGST\b|\bIGST\b|CBDT|\bTDS\b|EBANKING\/GST|EBA[A-Z]*\/GST|^GST\/|INT\.?\s*PAI?D|MIN\s*BAL)/i.test(upper)) {
    result.mode = "BANK";
    return finish(result);
  }
  // ATM / cash withdrawal (NWD = HDFC ATM withdrawal, nfs = shared ATM network)
  if (/^(NWD|ATW|ATM|NFS|POS)\b|^nfs\//i.test(text)) {
    result.mode = "ATM";
    return finish(result);
  }
  // IMPS/<ref>/<name>/<bank>/XX1234/<remark>   or HDFC  IMPS-<ref>-<name>-<bank>-XXXX1234-<remark>
  if (/^IMPS[/\-]/i.test(text)) {
    const parts = text.split(text.includes("/") ? /[/]/ : /-/);
    result.mode = "IMPS";
    result.ref = parts.find((p) => isRef(p)) || "";
    result.name = firstName(parts.slice(1));
    result.bank = (parts.find((p) => BANK_CODES.test(clean(p)) && !/^BANK/i.test(clean(p))) || "").slice(0, 4).toUpperCase();
    return finish(result);
  }
  // MPAY/INST/NEFT/<ref>/<account>/<name>   (IDBI mobile banking)
  if (/^MPAY\//i.test(text)) {
    const parts = text.split("/");
    result.mode = /NEFT/i.test(text) ? "NEFT" : /IMPS/i.test(text) ? "IMPS" : "OTHER";
    result.ref = parts.find((p) => isRef(p)) || "";
    result.name = firstName(parts.slice(3).reverse());
    return finish(result);
  }
  // NEFT-<UTR>-<name>   NEFT/<UTR>/<name>   RTGS-<UTR>-<name>
  const neft = text.match(/^(NEFT|RTGS)\s*[-/:]\s*([A-Z0-9]+)\s*[-/:]\s*(.+)$/i);
  if (neft) {
    result.mode = neft[1].toUpperCase();
    result.ref = neft[2];
    result.name = firstName(neft[3].split(/[-/]/));
    return finish(result);
  }
  // UPI/<ref>/<name>/<bank or vpa>/...   or  UPI-<name>-<vpa>-<ifsc>-<ref>-UPI (HDFC)
  if (/^UPI[/\-]/i.test(text)) {
    result.mode = "UPI";
    const parts = text.split(/[/]/).length > 2 ? text.split("/") : text.split("-");
    result.ref = parts.find((p) => isRef(p)) || "";
    result.name = firstName(parts.slice(1));
    // Only a UPI ID? its part before "@" is the best name we have (e.g. "ravi.kumar")
    if (!result.name && result.vpa) result.name = result.vpa.split("@")[0].replace(/[._\-\d]+/g, " ");
    return finish(result);
  }
  // HDFC third-party transfer: <ref>-TPT-<remark>-<NAME>
  const tpt = text.match(/^(\d+)-TPT-(.+)$/i);
  if (tpt) {
    result.mode = "NEFT";
    result.ref = tpt[1];
    result.name = firstName(tpt[2].split("-").reverse());
    return finish(result);
  }
  // Mobile-banking self transfer: MBank/<ref>/Self by/to <name>
  if (/^(MBANK|MBA)[^/]*\/[^/]*\/SEL/i.test(text)) {
    result.mode = "SELF";
    result.ref = (text.split("/").find((p) => isRef(p))) || "";
    return finish(result);
  }
  // Mobile / internet banking transfers: MBank/IMPS/.../<name>, eBanking/...
  if (/^(MBANK|MBA|EBANK|INB|IB)[^A-Z]?/i.test(text)) {
    const parts = text.split(/[/]/);
    result.mode = /IMPS/i.test(text) ? "IMPS" : /NEFT/i.test(text) ? "NEFT" : "OTHER";
    result.ref = parts.find((p) => isRef(p)) || "";
    result.name = firstName(parts.slice(1).filter((p) => !/^(SEL|SELL|BYV|TOV|ATO)/i.test(p)));
    return finish(result);
  }
  if (/^(CHQ|CHEQUE|CLG|CLEARING|TO CLG|BY CLG)\b/i.test(upper) || /\bCHQ\b/i.test(upper)) {
    result.mode = "CHEQUE";
    result.ref = (text.match(/\b(\d{6})\b/) || [])[1] || "";
    result.name = firstName(text.replace(/\b(CHQ|CHEQUE|PAID|DEP|DEPOSIT|CLG)\b/gi, " ").split(/\s{2,}|[-/]/));
    return finish(result);
  }
  if (/^(SELF|CASH|BY CASH|CASH DEP|CSH)/i.test(upper)) {
    result.mode = "CASH";
    return finish(result);
  }
  // Fallback: the longest run of letters
  const words = text.split(/[^A-Za-z ]+/).map(clean).filter((w) => w.length >= 3 && !isNoise(w));
  result.name = words.sort((a, b) => b.length - a.length)[0] || "";
  return finish(result);
}

function finish(result) {
  result.name = clean(result.name).replace(/\b(BUSINE|BUSINESS|IMPS)$/i, "").trim();
  result.key = normalizeName(result.name);
  return result;
}
